import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import { OrganizationsPage } from '@organizations/OrganizationsPage.tsx';
import { LocalizationProvider } from '@localization/LocalizationProvider';
import { BusinessPartnersPage } from './ui/pages/business-partners/BusinessPartnersPage';
import { PlaygroundPage } from './ui/pages/playground/PlaygroundPage';
import { TranslationEditorPage } from './ui/pages/translation-editor/TranslationEditorPage';
import { DocumentTitle } from '@commonComponents/core/DocumentTitle.tsx';
import { SalutationsPage } from './ui/pages/salutations/SalutationsPage';
import { QuantityUnitsPage } from './ui/pages/quantity-units/QuantityUnitsPage';
import { useEffect, useState } from 'react';
import { registerGridPlugins } from '@commonComponents/data/grid/CdfGrid/plugins';
import { logger } from '@utils/logging-utils';
import { NumberRangesPage } from './ui/pages/number-ranges/NumberRangesPage.tsx';
import { FunctionsPage } from './ui/pages/functions/FunctionsPage.tsx';
import { TextPage } from './ui/pages/texts/TextPage.tsx';
import { FreeFieldsPage } from './ui/pages/free-fields/FreeFieldsPage.tsx';
import { ReportImagesPage } from './ui/pages/report-images/ReportImagesPage';
import { useTranslation } from 'react-i18next';
import { useAuth, getIsLogoutInProgress } from '@hooks/useAuth';
import { msalInstance } from '@auth/msal-instance';
import { Home } from './ui/pages/home/Home.tsx';
import { useDispatch } from 'react-redux';
import { setBrzFinanceEnabled, setBrz8FinanceEnabled } from '@state/app-context-slice';
import { AnimatedRoute } from '@sharedComponents/animatedroute/AnimatedRoute';
import { AppFrame } from './ui/components/shared/layout/AppFrame';
const playgroundEnabled = import.meta.env.VITE_PLAYGROUND_ENABLED === 'true';
const translationEditorEnabled = import.meta.env.VITE_TRANSLATION_EDITOR_ENABLED === 'true' || false;

/**
 * Component that automatically redirects unauthenticated users to Microsoft login
 */
function UnauthenticatedRedirect(): React.ReactNode {
  const { login } = useAuth();
  const [hasTriggeredLogin, setHasTriggeredLogin] = useState(false);

  useEffect(() => {
    // Add a small delay to allow AuthProvider to complete initialization
    const timer = setTimeout(() => {
      // Don't trigger login if logout is in progress
      if (getIsLogoutInProgress()) {
        logger.info('Auth', 'Logout in progress, skipping auto-login');
        return;
      }

      // Check if we already have an active account or if authentication is in progress
      const activeAccount = msalInstance.getActiveAccount();
      const interactionInProgress = localStorage.getItem('msal.interaction.status') === 'interaction_in_progress';

      if (activeAccount) {
        logger.info('Auth', 'Active account found, skipping login redirect');
        return;
      }

      if (interactionInProgress) {
        logger.info('Auth', 'Authentication interaction already in progress, skipping login redirect');
        return;
      }

      const forgotPasswordInUrl = globalThis.location.hash.includes('AADB2C90118') || globalThis.location.search.includes('AADB2C90118');
      if (forgotPasswordInUrl) {
        logger.info('Auth', 'Forgot password flow detected, skipping default login redirect');
        return;
      }

      if (!hasTriggeredLogin) {
        logger.info('Auth', 'No active account found, triggering login redirect');
        setHasTriggeredLogin(true);

        // Automatically trigger Microsoft login redirect for unauthenticated users
        login().catch(error => {
          logger.error('Auth', 'Failed to redirect to Microsoft login', { error });
          // Reset flag on error to allow retry
          setHasTriggeredLogin(false);
        });
      }
    }, 100); // Small delay to allow AuthProvider initialization

    return () => clearTimeout(timer);
  }, [login, hasTriggeredLogin]);

  // Return empty fragment while redirecting
  return <></>;
}

/**
 * Main application component for BRZ365 CoreElement UI.
 *
 * Provides context providers, routing, and layout for the entire app.
 * Handles authentication templates, localization, and dynamic feature routes.
 *
 * - Wraps all content in LocalizationProvider for i18n support.
 * - Sets the document title.
 * - Registers grid plugins on mount.
 * - Uses BrowserRouter for client-side routing.
 * - Automatically redirects unauthenticated users to login.
 * - Redirects authenticated users to business partners by default.
 * - All user-facing strings are internationalized.
 * - Automatically redirects unauthenticated users to Microsoft login.
 * - Initializes BRZ Finance state to enabled by default.
 *
 * @returns The root JSX element for the application.
 */
export function App(): React.ReactNode {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // Initialize grid plugins
  useEffect(() => {
    logger.info('App', 'Initializing grid plugins');
    registerGridPlugins();
  }, []);

  // Initialize BRZ Finance state to enabled when app starts
  useEffect(() => {
    logger.info('App', 'Initializing BRZ Finance state to enabled');
    dispatch(setBrzFinanceEnabled(false));
    dispatch(setBrz8FinanceEnabled(false));
  }, [dispatch]);

  return (
    <LocalizationProvider enableBrowserDetection={true} autoUpdateOnMismatch={true}>
      <DocumentTitle />
      <BrowserRouter>
        <AuthenticatedTemplate>
          <AppFrame>
            <AnimatedRoute>
              <Routes>
                <Route path={'/'} element={<Home />} />
                <Route path={'/business-partners'} element={<BusinessPartnersPage />} />
                <Route path={'/organizations'} element={<OrganizationsPage />} />
                <Route path={'/salutations'} element={<SalutationsPage />} />
                <Route path={'/functions'} element={<FunctionsPage />} />
                <Route path={'/quantity-units'} element={<QuantityUnitsPage />} />
                <Route path={'/number-ranges'} element={<NumberRangesPage />} />
                <Route path={'/texts'} element={<TextPage />} />
                <Route path={'/free-fields'} element={<FreeFieldsPage />} />
                <Route path={'/report-images'} element={<ReportImagesPage />} />
                {playgroundEnabled && <Route path={'/playground'} element={<PlaygroundPage />} />}
                {translationEditorEnabled && <Route path={'/translation-editor'} element={<TranslationEditorPage />} />}
                <Route path="*" element={<p>{t('errors.notFound')}</p>} />
              </Routes>
            </AnimatedRoute>
          </AppFrame>
        </AuthenticatedTemplate>
        <UnauthenticatedTemplate>
          <UnauthenticatedRedirect />
        </UnauthenticatedTemplate>
      </BrowserRouter>
    </LocalizationProvider>
  );
}